import { ApolloError } from '@apollo/client';

import {
  GetCatalogProductViewQuery,
  CatalogVariantFragmentFragment,
} from '@notino/shared/definitions/types';
import {
  AdditionalServicesAvailability,
  LoyaltyDiscount,
} from '@notino/web-tracking';

import { IEngraving } from './../../ProductListing/model';
import { BrandsId } from './model';
import { IModifaceEffectVariants } from './ModiFaceModal/model';

export const getModifaceEffectsForVariant = (
  modifaceMakeUpData: GetCatalogProductViewQuery['productDetailByCatalogMasterId']['variants'][number]['features']['makeUpVirtualTryOnEffects']
) => {
  return modifaceMakeUpData.shades.map((shade) => ({
    ...shade,
    category: modifaceMakeUpData.category,
  }));
};

export const getModifaceVariants = (
  variants: GetCatalogProductViewQuery['productDetailByCatalogMasterId']['variants']
): IModifaceEffectVariants => {
  return variants.reduce(
    (result, { webId, features: { makeUpVirtualTryOnEffects } }) => {
      if (!makeUpVirtualTryOnEffects) {
        return result;
      }

      // If it has modiface effect
      return {
        ...result,
        [webId]: getModifaceEffectsForVariant(makeUpVirtualTryOnEffects),
      };
    },
    <IModifaceEffectVariants>{}
  );
};

export const getModifaceAvailability = (
  hasModiface: boolean
): AdditionalServicesAvailability =>
  hasModiface
    ? AdditionalServicesAvailability.available
    : AdditionalServicesAvailability.notAvailable;

export const getTryItFirstAvailability = (
  cartTryItFirstShow: boolean,
  tryItFirstCampaigns: GetCatalogProductViewQuery['productDetailByCatalogMasterId']['campaigns'],
  selectedVariant: CatalogVariantFragmentFragment,
  additionalServicesError: ApolloError
) => {
  if (additionalServicesError) {
    return AdditionalServicesAvailability.timeout;
  }

  if (!cartTryItFirstShow) {
    return AdditionalServicesAvailability.hidden;
  }

  if (
    tryItFirstCampaigns.some(
      (tryItFirstCampaign) =>
        tryItFirstCampaign.productId === selectedVariant.webId
    )
  ) {
    return AdditionalServicesAvailability.available;
  }

  return AdditionalServicesAvailability.notAvailable;
};

export const getEngravingAvailability = (
  cartEngravingShow: boolean,
  engraving: IEngraving,
  additionalServicesError: ApolloError
): AdditionalServicesAvailability => {
  if (additionalServicesError) {
    return AdditionalServicesAvailability.timeout;
  }

  if (engraving?.allowed) {
    return cartEngravingShow && engraving.config.isAvailable
      ? AdditionalServicesAvailability.available
      : AdditionalServicesAvailability.hidden;
  }

  return AdditionalServicesAvailability.notAvailable;
};

export const getVariantDescription = (variant, product): string =>
  variant.attributes && variant.attributes.Master
    ? product.annotation
    : variant.variantName;

export const getLoyaltyDiscountState = (
  customerBenefits: GetCatalogProductViewQuery['productDetailByCatalogMasterId']['variants'][number]['features']['customerBenefits']
) => {
  if (customerBenefits?.availableDiscount) {
    return LoyaltyDiscount.available;
  }

  if (customerBenefits?.customerDiscount) {
    return LoyaltyDiscount.active;
  }

  return LoyaltyDiscount.notAvailable;
};

export const isGiftAvailable = (
  campaigns: GetCatalogProductViewQuery['productDetailByCatalogMasterId']['campaigns']
) => {
  return Boolean(
    campaigns.find(
      (campaign) =>
        campaign.displayOnDetailPage &&
        (campaign.detailPageText || campaign.text)
    )
  );
};

export const getModifaceHairEffectsForVariant = (
  effect: GetCatalogProductViewQuery['productDetailByCatalogMasterId']['variants'][number]['features']['hairVirtualTryOnModel']
) => {
  return effect.shades.map((shade) => ({
    ...shade,
    category: effect.category,
    histogram: {
      r: shade.histogram_r,
      g: shade.histogram_g,
      b: shade.histogram_b,
      c: shade.histogram_c,
      totalpixels: shade.totalpixels,
    },
  }));
};

export const getModifaceHairVariants = (
  variants: GetCatalogProductViewQuery['productDetailByCatalogMasterId']['variants']
): IModifaceEffectVariants => {
  return variants.reduce(
    (result, { webId, features: { hairVirtualTryOnModel } }) => {
      if (!hairVirtualTryOnModel) {
        return result;
      }
      return {
        ...result,
        [webId]: getModifaceHairEffectsForVariant(hairVirtualTryOnModel),
      };
    },
    {} as IModifaceEffectVariants
  );
};

export const isDiorBrand = (brandId: string) => brandId === BrandsId.DIOR;
